import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { BuildingCombobox } from '../components/BuildingCombobox';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { downloadPdf as downloadPdfUtil } from '../utils/pdfUtils';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Plus, Trash, PencilSimple, MagnifyingGlass, Warning, Broom, UserCircle, CaretDown, CaretUp, CaretRight, GearSix, FilePdf, FunnelSimple, Lightning, SortAscending, SortDescending } from '@phosphor-icons/react';
import { toast } from 'sonner';
import DatePickerDMY from '../components/DatePickerDMY';
import DateModeSelector from '../components/DateModeSelector';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const axCfg = { withCredentials: true };

const TYPE_COLORS = [
  'bg-blue-50 text-blue-700 border-blue-200',
  'bg-cyan-50 text-cyan-700 border-cyan-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-violet-50 text-violet-700 border-violet-200',
  'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-rose-50 text-rose-700 border-rose-200',
  'bg-sky-50 text-sky-700 border-sky-200',
  'bg-orange-50 text-orange-700 border-orange-200',
  'bg-teal-50 text-teal-700 border-teal-200',
  'bg-zinc-50 text-zinc-700 border-zinc-200',
];

const getTypeColor = (type, cleaningTypes) => {
  const idx = cleaningTypes.findIndex(ct => ct.key === type);
  return TYPE_COLORS[idx >= 0 ? idx % TYPE_COLORS.length : TYPE_COLORS.length - 1];
};

const formatDateDMY = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

export default function CleaningHistory({ embedded = false, presetCleaner = null, openAddSignal = 0 }) {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [entries, setEntries] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [globalCleaners, setGlobalCleaners] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterBuilding, setFilterBuilding] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterCleaner, setFilterCleaner] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateMode, setDateMode] = useState('all');
  // Phase 366 — sort order toggle (default oldest-first).
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState('');

  // Add/Edit entry dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    building_id: '', date: new Date().toISOString().split('T')[0], notes: '',
    sections: [{ cleaning_type: 'general_cleaning', cleaners: [{ employee_name: '', cleaner_id: '', cleaner_payment: '' }] }],
  });

  // Delete entry dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);

  // Alerts toggle
  const [showAlerts, setShowAlerts] = useState(false);

  // Quick Log form (inline)
  const [quickLogOpen, setQuickLogOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({
    building_id: '', date: new Date().toISOString().split('T')[0], notes: '',
    sections: [{ cleaning_type: 'general_cleaning', cleaners: [{ employee_name: '', cleaner_id: '', cleaner_payment: '' }] }],
  });
  const [quickSubmitting, setQuickSubmitting] = useState(false);

  // Statement
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtCleaner, setStmtCleaner] = useState('');
  const [stmtFrom, setStmtFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; });
  const [stmtTo, setStmtTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [stmtData, setStmtData] = useState(null);
  const [stmtLoading, setStmtLoading] = useState(false);

  // Dynamic cleaning types
  const [cleaningTypes, setCleaningTypes] = useState([]);
  const [typeMgmtOpen, setTypeMgmtOpen] = useState(false);
  const [typeForm, setTypeForm] = useState({ key: '', name_en: '', name_el: '', threshold_days: 0 });
  const [editingType, setEditingType] = useState(null);
  const [deleteTypeDialogOpen, setDeleteTypeDialogOpen] = useState(false);
  const [deletingType, setDeletingType] = useState(null);

  const getTypeName = (key) => {
    const ct = cleaningTypes.find(c => c.key === key);
    if (!ct) return t(key) || key;
    return language === 'el' ? (ct.name_el || ct.name_en) : ct.name_en;
  };

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterBuilding !== 'all') params.append('building_id', filterBuilding);
      if (filterType !== 'all') params.append('cleaning_type', filterType);
      if (filterCleaner !== 'all') params.append('employee_name', filterCleaner);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [entriesRes, buildingsRes, alertsRes, cleanersRes, typesRes] = await Promise.all([
        axios.get(`${API}/cleaning-history${qs}`, axCfg),
        axios.get(`${API}/buildings`, axCfg),
        axios.get(`${API}/cleaning-alerts`, axCfg),
        axios.get(`${API}/cleaning/cleaners`, axCfg),
        axios.get(`${API}/cleaning-types`, axCfg),
      ]);
      setEntries(entriesRes.data);
      setBuildings(buildingsRes.data);
      setAlerts(alertsRes.data);
      setGlobalCleaners(cleanersRes.data);
      setCleaningTypes(typesRes.data);
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [filterBuilding, filterType, filterCleaner, dateFrom, dateTo, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filteredEntries = useMemo(() => entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      e.building_name?.toLowerCase().includes(s) ||
      e.employee_name?.toLowerCase().includes(s) ||
      e.notes?.toLowerCase().includes(s) ||
      getTypeName(e.cleaning_type)?.toLowerCase().includes(s)
    );
  }).sort((a, b) => {
    // Phase 366 — Sort by date according to user-selected order, then by building name within same date.
    const da = a.date || '', db = b.date || '';
    const dateCompare = sortOrder === 'asc' ? da.localeCompare(db) : db.localeCompare(da);
    if (dateCompare !== 0) return dateCompare;
    return (a.building_name || '').localeCompare(b.building_name || '');
  }), [entries, search, cleaningTypes, language, sortOrder]);

  // Payment & Profit totals
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [groupMode, setGroupMode] = useState('date'); // 'date' or 'building'
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const paymentTotal = filteredEntries.reduce((sum, e) => sum + (e.cleaner_payment || 0), 0);
  const avgPerJob = filteredEntries.length > 0 ? paymentTotal / filteredEntries.length : 0;

  // Current month total
  const currentMonthTotal = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return filteredEntries.filter(e => (e.date || '').startsWith(ym)).reduce((s, e) => s + (e.cleaner_payment || 0), 0);
  }, [filteredEntries]);

  const monthlyBreakdown = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(e => {
      if (!e.cleaner_payment) return;
      const [year, month] = (e.date || '').split('-');
      if (!year || !month) return;
      const key = `${year}-${month}`;
      groups[key] = (groups[key] || 0) + e.cleaner_payment;
    });
    return Object.entries(groups)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, total]) => {
        const [y, m] = key.split('-');
        const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthNamesEl = ['', 'Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];
        return { label: `${monthNames[parseInt(m)]} ${y}`, labelEl: `${monthNamesEl[parseInt(m)]} ${y}`, total };
      });
  }, [filteredEntries]);

  // Grouped data computation
  const groupedData = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(e => {
      const key = groupMode === 'date' ? (e.date || 'unknown') : (e.building_name || 'unknown');
      if (!groups[key]) groups[key] = { entries: [], total: 0, key };
      groups[key].entries.push(e);
      groups[key].total += (e.cleaner_payment || 0);
    });
    return Object.values(groups).sort((a, b) => {
      if (groupMode === 'date') return b.key.localeCompare(a.key);
      return a.key.localeCompare(b.key);
    });
  }, [filteredEntries, groupMode]);

  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  const collapseAllGroups = () => {
    const all = {};
    groupedData.forEach(g => { all[g.key] = true; });
    setCollapsedGroups(all);
  };
  const expandAllGroups = () => setCollapsedGroups({});

  // ENTRY CRUD
  const openAddDialog = () => {
    setEditingEntry(null);
    const selectedCleaner = filterCleaner !== 'all' ? globalCleaners.find(c => c.name === filterCleaner) : null;
    setFormData({
      building_id: filterBuilding !== 'all' ? filterBuilding : '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      sections: [{
        cleaning_type: filterType !== 'all' ? filterType : 'general_cleaning',
        cleaners: [{
          employee_name: filterCleaner !== 'all' ? filterCleaner : '',
          cleaner_id: selectedCleaner ? selectedCleaner.cleaner_id : '',
          cleaner_payment: '',
        }],
      }],
    });
    setDialogOpen(true);
  };

  // Open the Add dialog with a preselected cleaner. Used when the embedded
  // view is launched from the Cleaner profile via the "Add Building Cleaning"
  // shortcut so the user lands directly on a ready-to-fill row for that
  // cleaner without having to pick them again.
  const openAddDialogForCleaner = useCallback((cleaner) => {
    setEditingEntry(null);
    setFormData({
      building_id: filterBuilding !== 'all' ? filterBuilding : '',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      sections: [{
        cleaning_type: 'general_cleaning',
        cleaners: [{
          employee_name: cleaner?.name || '',
          cleaner_id: cleaner?.cleaner_id || '',
          cleaner_payment: '',
        }],
      }],
    });
    setDialogOpen(true);
  }, [filterBuilding]);

  // React to external "open Add dialog" signal from the parent (Cleaning
  // Management → Cleaner profile → Add Building Cleaning). We watch the
  // signal counter so each click triggers a fresh open even if the dialog
  // was previously closed.
  useEffect(() => {
    if (openAddSignal && openAddSignal > 0) {
      openAddDialogForCleaner(presetCleaner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddSignal]);

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    const matchedCleaner = globalCleaners.find(c => c.name === entry.employee_name || c.cleaner_id === entry.cleaner_id);
    setFormData({
      building_id: entry.building_id,
      date: entry.date,
      notes: entry.notes || '',
      sections: [{
        cleaning_type: entry.cleaning_type || 'general_cleaning',
        cleaners: [{
          employee_name: entry.employee_name || '',
          cleaner_id: entry.cleaner_id || (matchedCleaner ? matchedCleaner.cleaner_id : ''),
          cleaner_payment: entry.cleaner_payment != null ? String(entry.cleaner_payment) : '',
        }],
      }],
    });
    setDialogOpen(true);
  };

  // Phase 264 — Bug fix: the trash button was calling `openDeleteDialog`
  // which was never defined, so clicking "Delete" did nothing (user
  // report: "at cleaning history i cannot delete data"). Define it here
  // so it opens the existing confirmation dialog wired to `handleDelete`.
  const openDeleteDialog = (entry) => {
    setDeletingEntry(entry);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        // Edit mode: single entry update (1 section, 1 cleaner)
        const sec = formData.sections[0] || {};
        const c = (sec.cleaners || [])[0] || {};
        const cleanerMatch = globalCleaners.find(gc => gc.name === c.employee_name);
        const cleanerId = c.cleaner_id || (cleanerMatch ? cleanerMatch.cleaner_id : null);
        await axios.put(`${API}/cleaning-history/${editingEntry.entry_id}`, {
          date: formData.date, cleaning_type: sec.cleaning_type || 'general_cleaning',
          employee_name: c.employee_name, cleaner_id: cleanerId, notes: formData.notes,
          cleaner_payment: c.cleaner_payment ? parseFloat(c.cleaner_payment) : null,
        }, axCfg);
      } else {
        // Add mode: batch create across all sections
        const sectionsPayload = [];
        for (const sec of formData.sections) {
          const validCleaners = (sec.cleaners || []).filter(c => c.employee_name);
          for (const c of validCleaners) {
            const cleanerMatch = globalCleaners.find(gc => gc.name === c.employee_name);
            sectionsPayload.push({
              cleaning_type: sec.cleaning_type || 'general_cleaning',
              employee_name: c.employee_name,
              cleaner_id: c.cleaner_id || (cleanerMatch ? cleanerMatch.cleaner_id : null),
              cleaner_payment: c.cleaner_payment ? parseFloat(c.cleaner_payment) : null,
            });
          }
        }
        if (sectionsPayload.length === 0) { toast.error(t('selectCleaner')); return; }

        // Use batch endpoint with sections
        await axios.post(`${API}/cleaning-history/batch`, {
          building_id: formData.building_id, date: formData.date,
          notes: formData.notes, cleaners: sectionsPayload,
        }, axCfg);
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/cleaning-history/${deletingEntry.entry_id}`, axCfg);
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      setDeletingEntry(null);
      fetchData();
    } catch { toast.error(t('errorOccurred')); }
  };

  const handleQuickLog = async (buildingId) => {
    try {
      await axios.post(`${API}/cleaning-history/quick-add?building_id=${buildingId}`, {}, axCfg);
      toast.success(t('cleanedToday'));
      fetchData();
    } catch { toast.error(t('errorOccurred')); }
  };

  // Quick Log form submit
  const handleQuickLogSubmit = async (e) => {
    e.preventDefault();
    const allCleaners = [];
    for (const sec of quickForm.sections) {
      const valid = (sec.cleaners || []).filter(c => c.employee_name);
      for (const c of valid) {
        const match = globalCleaners.find(gc => gc.name === c.employee_name);
        allCleaners.push({
          cleaning_type: sec.cleaning_type || 'general_cleaning',
          employee_name: c.employee_name,
          cleaner_id: c.cleaner_id || (match ? match.cleaner_id : null),
          cleaner_payment: c.cleaner_payment ? parseFloat(c.cleaner_payment) : null,
        });
      }
    }
    if (!quickForm.building_id || allCleaners.length === 0) { toast.error(t('selectCleaner') + ' & ' + t('buildings')); return; }
    setQuickSubmitting(true);
    try {
      await axios.post(`${API}/cleaning-history/batch`, {
        building_id: quickForm.building_id, date: quickForm.date,
        notes: quickForm.notes, cleaners: allCleaners,
      }, axCfg);
      toast.success(t('savedSuccess'));
      setQuickForm({
        building_id: '', date: new Date().toISOString().split('T')[0], notes: '',
        sections: [{ cleaning_type: 'general_cleaning', cleaners: [{ employee_name: '', cleaner_id: '', cleaner_payment: '' }] }],
      });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
    finally { setQuickSubmitting(false); }
  };

  // Statement from cleaning history
  const loadStatement = async () => {
    setStmtLoading(true);
    try {
      const params = new URLSearchParams();
      if (stmtCleaner) params.append('employee_name', stmtCleaner);
      if (stmtFrom) params.append('start_date', stmtFrom);
      if (stmtTo) params.append('end_date', stmtTo);
      const res = await axios.get(`${API}/cleaning-history/statement?${params}`, axCfg);
      setStmtData(res.data);
    } catch { toast.error(t('errorOccurred')); }
    finally { setStmtLoading(false); }
  };

  const handleDownloadStatementPdf = () => {
    const params = {};
    if (stmtCleaner) params.employee_name = stmtCleaner;
    if (stmtFrom) params.start_date = stmtFrom;
    if (stmtTo) params.end_date = stmtTo;
    downloadPdfUtil('/cleaning-history/statement/pdf', params);
  };

  // CLEANING TYPE CRUD
  const handleSaveType = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await axios.put(`${API}/cleaning-types/${editingType.type_id}`, typeForm, axCfg);
        setEditingType(null);
      } else {
        const key = typeForm.key || typeForm.name_en.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        await axios.post(`${API}/cleaning-types`, { ...typeForm, key }, axCfg);
      }
      toast.success(t('savedSuccess'));
      setTypeForm({ key: '', name_en: '', name_el: '', threshold_days: 0 });
      const res = await axios.get(`${API}/cleaning-types`, axCfg);
      setCleaningTypes(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDeleteType = async () => {
    try {
      await axios.delete(`${API}/cleaning-types/${deletingType.type_id}`, axCfg);
      toast.success(t('deletedSuccess'));
      setDeleteTypeDialogOpen(false);
      setDeletingType(null);
      const res = await axios.get(`${API}/cleaning-types`, axCfg);
      setCleaningTypes(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || t('typeUsedInRecords')); }
  };

  if (loading) {
    const loadingContent = <div className="flex items-center justify-center h-64"><div className="text-zinc-500">{t('loading')}</div></div>;
    if (embedded) return loadingContent;
    return <Layout>{loadingContent}</Layout>;
  }

  const content = (
      <div data-testid="cleaning-history-page">
        {/* Header: count + search + filters + buttons (matches Job History) */}
        <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-sm text-zinc-500">{filteredEntries.length} {t('totalEntries').toLowerCase()}</p>
            <div className="relative"><MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('search')} className="rounded-none pl-8 h-8 w-48 text-xs" data-testid="cleaning-search" /></div>
            <Select value={filterCleaner} onValueChange={setFilterCleaner}><SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="cleaning-cleaner-filter"><FunnelSimple size={14} className="mr-1" /><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t('allCleaners')}</SelectItem>{globalCleaners.map(c => <SelectItem key={c.cleaner_id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select>
            <Select value={filterBuilding} onValueChange={setFilterBuilding}><SelectTrigger className="rounded-none h-8 w-44 text-xs" data-testid="cleaning-building-filter"><FunnelSimple size={14} className="mr-1" /><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t('all')} {t('buildings')}</SelectItem>{buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}</SelectContent></Select>
            <Select value={filterType} onValueChange={setFilterType}><SelectTrigger className="rounded-none h-8 w-40 text-xs" data-testid="cleaning-type-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{t('allTypes')}</SelectItem>{cleaningTypes.map(ct => <SelectItem key={ct.key} value={ct.key}>{getTypeName(ct.key)}</SelectItem>)}</SelectContent></Select>
            <DateModeSelector
              mode={dateMode}
              setMode={setDateMode}
              from={dateFrom}
              to={dateTo}
              onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
              allowAll
              showLabels={false}
              testIdPrefix="cleaning-datemode"
              size="sm"
            />
            <button
              type="button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 border border-zinc-300 bg-white text-xs text-zinc-700 hover:bg-zinc-50 rounded-none"
              data-testid="cleaning-sort-toggle"
              title={sortOrder === 'asc' ? (t('oldestFirst') || 'Oldest first') : (t('newestFirst') || 'Newest first')}
            >
              {sortOrder === 'asc' ? <SortAscending size={14} /> : <SortDescending size={14} />}
              <span className="font-medium">{sortOrder === 'asc' ? (t('oldestFirst') || 'Oldest first') : (t('newestFirst') || 'Newest first')}</span>
            </button>
          </div>
          <div className="flex gap-2">
            {isAdmin && <Button variant="outline" size="sm" onClick={() => setTypeMgmtOpen(true)} className="rounded-none" data-testid="manage-types-btn"><GearSix size={16} className="mr-1" /> {t('manageTypes')}</Button>}
            <Button variant="outline" size="sm" onClick={() => { setStmtOpen(true); loadStatement(); }} className="rounded-none" data-testid="statement-btn"><FilePdf size={16} className="mr-1" /> {t('exportPdf')}</Button>
            <Button onClick={openAddDialog} size="sm" className="rounded-none" data-testid="add-cleaning-btn"><Plus size={16} className="mr-1" /> {t('addCleaningEntry')}</Button>
          </div>
        </div>

        {/* Summary Cards */}
        {filteredEntries.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="cleaning-summary-cards">
            <div className="bg-white border border-zinc-200 p-4">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalEntries')}</span>
              <p className="text-xl font-mono font-bold mt-1" data-testid="summary-total-entries">{filteredEntries.length}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-4">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalPayment')}</span>
              <p className="text-xl font-mono font-bold text-amber-700 mt-1" data-testid="summary-total-payment">&euro;{paymentTotal.toFixed(2)}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-4">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('averagePerJob')}</span>
              <p className="text-xl font-mono font-bold text-blue-700 mt-1" data-testid="summary-avg-per-job">&euro;{avgPerJob.toFixed(2)}</p>
            </div>
            <div className="bg-white border border-zinc-200 p-4">
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">{t('monthlyTotal')}</span>
              <p className="text-xl font-mono font-bold text-emerald-700 mt-1" data-testid="summary-monthly-total">&euro;{currentMonthTotal.toFixed(2)}</p>
            </div>
          </div>
        )}

        {/* Flat Table (like Job History) */}
        <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="cleaning-table-container">
          <table className="w-full text-sm" data-testid="cleaning-table">
            <thead><tr className="bg-zinc-50 border-b border-zinc-200 text-[10px] text-zinc-400 uppercase">
              <th className="text-left px-4 py-2.5 font-medium">{t('date')}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('buildings')}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('cleanerName')}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('cleaningType')}</th>
              <th className="text-right px-4 py-2.5 font-medium">{t('cleanerPayment')}</th>
              <th className="text-left px-4 py-2.5 font-medium">{t('notes')}</th>
              <th className="w-20 px-4 py-2.5"></th>
            </tr></thead>
            <tbody>
              {filteredEntries.map(entry => (
                <tr key={entry.entry_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`cleaning-row-${entry.entry_id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{formatDateDMY(entry.date)}</td>
                  <td className="px-4 py-2.5 font-medium text-sm">{entry.building_name}</td>
                  <td className="px-4 py-2.5 text-sm">{entry.employee_name || '-'}</td>
                  <td className="px-4 py-2.5"><span className={`px-1.5 py-0.5 text-[10px] font-semibold border ${getTypeColor(entry.cleaning_type || 'general_cleaning', cleaningTypes)}`}>{getTypeName(entry.cleaning_type || 'general_cleaning')}</span></td>
                  <td className="px-4 py-2.5 text-right font-mono text-amber-700 font-semibold">&euro;{(entry.cleaner_payment || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-zinc-400 max-w-[150px] truncate text-xs">{entry.notes || entry.other_notes || '-'}</td>
                  <td className="px-4 py-2.5"><div className="flex gap-1"><button onClick={() => openEditDialog(entry)} className="text-zinc-300 hover:text-zinc-700 transition-colors"><PencilSimple size={14} /></button><button onClick={() => openDeleteDialog(entry)} className="text-zinc-300 hover:text-red-600 transition-colors"><Trash size={14} /></button></div></td>
                </tr>
              ))}
              {filteredEntries.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-zinc-400 text-sm">{t('noCleaning')}</td></tr>}
            </tbody>
            {filteredEntries.length > 0 && (
              <tfoot><tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold text-xs">
                <td colSpan={4} className="px-4 py-2.5 text-right uppercase text-zinc-400">{t('total')} ({filteredEntries.length} {t('entries')})</td>
                <td className="px-4 py-2.5 text-right font-mono text-amber-700 text-sm">&euro;{paymentTotal.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr></tfoot>
            )}
          </table>
        </div>
        {/* Quick Log Section */}
        <div className="mt-6 bg-white border border-zinc-200" data-testid="quick-log-section">
          <button onClick={() => setQuickLogOpen(!quickLogOpen)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-50 transition-colors">
            <div className="flex items-center gap-2">
              <Lightning size={18} className="text-amber-600" weight="fill" />
              <span className="text-sm font-semibold">{t('quickLog')}</span>
            </div>
            {quickLogOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
          {quickLogOpen && (
            <form onSubmit={handleQuickLogSubmit} className="border-t border-zinc-200 px-4 py-4" data-testid="quick-log-form">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end mb-3">
                <div>
                  <Label className="text-xs">{t('buildings')} *</Label>
                  <BuildingCombobox
                    buildings={buildings}
                    value={quickForm.building_id}
                    onValueChange={v => setQuickForm(f => ({ ...f, building_id: v }))}
                    placeholder={t('buildings')}
                    triggerClassName="h-9 text-xs"
                    testId="quick-building"
                  />
                </div>
                <div>
                  <Label className="text-xs">{t('date')}</Label>
                  <DatePickerDMY value={quickForm.date} onChange={v => setQuickForm(f => ({...f, date: v}))} className="rounded-none h-9 text-xs font-mono" data-testid="quick-date" />
                </div>
                <div className="flex items-end gap-2">
                  <button type="button" onClick={() => setQuickForm(f => ({ ...f, sections: [...f.sections, { cleaning_type: 'general_cleaning', cleaners: [{ employee_name: '', cleaner_id: '', cleaner_payment: '' }] }] }))} className="h-9 px-3 text-xs text-zinc-600 border border-zinc-300 hover:bg-zinc-50 flex items-center gap-1" data-testid="quick-add-type">
                    <Plus size={14} /> {t('addCleaningType')}
                  </button>
                </div>
              </div>
              {/* Quick Log Sections */}
              <div className="space-y-3 mb-3">
                {quickForm.sections.map((sec, sIdx) => (
                  <div key={sIdx} className="border border-zinc-200 bg-zinc-50/30" data-testid={`quick-section-${sIdx}`}>
                    <div className="px-3 py-1.5 border-b border-zinc-200 flex items-center gap-2">
                      <Select value={sec.cleaning_type} onValueChange={v => {
                        setQuickForm(f => { const upd = [...f.sections]; upd[sIdx] = { ...upd[sIdx], cleaning_type: v }; return { ...f, sections: upd }; });
                      }}>
                        <SelectTrigger className="rounded-none h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{cleaningTypes.map(ct => <SelectItem key={ct.key} value={ct.key}>{getTypeName(ct.key)}</SelectItem>)}</SelectContent>
                      </Select>
                      {quickForm.sections.length > 1 && (
                        <button type="button" onClick={() => setQuickForm(f => ({ ...f, sections: f.sections.filter((_, i) => i !== sIdx) }))} className="p-1 text-red-500 hover:bg-red-50"><Trash size={14} /></button>
                      )}
                    </div>
                    <div className="p-2 space-y-1.5">
                      {sec.cleaners.map((c, cIdx) => (
                        <div key={cIdx} className="flex items-center gap-2">
                          <div className="flex-1">
                            <Select value={c.employee_name} onValueChange={v => {
                              const match = globalCleaners.find(gc => gc.name === v);
                              setQuickForm(f => {
                                const upd = [...f.sections]; const cls = [...upd[sIdx].cleaners];
                                cls[cIdx] = { ...cls[cIdx], employee_name: v, cleaner_id: match ? match.cleaner_id : '' };
                                upd[sIdx] = { ...upd[sIdx], cleaners: cls }; return { ...f, sections: upd };
                              });
                            }}>
                              <SelectTrigger className="rounded-none h-8 text-xs"><SelectValue placeholder={t('selectCleaner') + ' *'} /></SelectTrigger>
                              <SelectContent>{globalCleaners.map(gc => <SelectItem key={gc.cleaner_id} value={gc.name}>{gc.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div className="w-24">
                            <Input type="number" step="0.01" value={c.cleaner_payment} onChange={e => {
                              setQuickForm(f => {
                                const upd = [...f.sections]; const cls = [...upd[sIdx].cleaners];
                                cls[cIdx] = { ...cls[cIdx], cleaner_payment: e.target.value };
                                upd[sIdx] = { ...upd[sIdx], cleaners: cls }; return { ...f, sections: upd };
                              });
                            }} className="rounded-none h-8 text-xs font-mono" placeholder="€0.00" />
                          </div>
                          {sec.cleaners.length > 1 && (
                            <button type="button" onClick={() => setQuickForm(f => {
                              const upd = [...f.sections]; upd[sIdx] = { ...upd[sIdx], cleaners: upd[sIdx].cleaners.filter((_, i) => i !== cIdx) };
                              return { ...f, sections: upd };
                            })} className="p-0.5 text-red-500 hover:bg-red-50"><Trash size={12} /></button>
                          )}
                        </div>
                      ))}
                      <button type="button" onClick={() => setQuickForm(f => {
                        const upd = [...f.sections]; upd[sIdx] = { ...upd[sIdx], cleaners: [...upd[sIdx].cleaners, { employee_name: '', cleaner_id: '', cleaner_payment: '' }] };
                        return { ...f, sections: upd };
                      })} className="text-[11px] text-blue-600 flex items-center gap-1 mt-1">
                        <Plus size={12} /> {t('addAnotherCleaner')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                {(() => {
                  const total = quickForm.sections.flatMap(s => s.cleaners).reduce((s, c) => s + (parseFloat(c.cleaner_payment) || 0), 0);
                  return total > 0 ? <span className="text-xs font-mono font-bold text-zinc-600" data-testid="quick-total">{t('totalPayment')}: &euro;{total.toFixed(2)}</span> : <span />;
                })()}
                <Button type="submit" disabled={quickSubmitting} className="bg-emerald-700 rounded-none h-9" data-testid="quick-submit">
                  <Lightning size={16} className="mr-1" /> {t('save')}
                </Button>
              </div>
            </form>
          )}
        </div>

        {/* ADD/EDIT ENTRY DIALOG */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="cleaning-entry-dialog">
            <DialogHeader>
              <DialogTitle>{editingEntry ? t('editCleaningEntry') : t('addCleaningEntry')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {!editingEntry ? (
                  <div>
                    <Label>{t('buildings')}</Label>
                    <BuildingCombobox
                      buildings={buildings}
                      value={formData.building_id}
                      onValueChange={(val) => setFormData({ ...formData, building_id: val })}
                      placeholder={t('buildings')}
                      triggerClassName="mt-1"
                      testId="cleaning-form-building"
                    />
                  </div>
                ) : <div />}
                <div>
                  <Label>{t('date')}</Label>
                  <DatePickerDMY value={formData.date} onChange={v => setFormData({ ...formData, date: v })} className="mt-1 font-mono" required data-testid="cleaning-form-date" />
                </div>
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="mt-1 rounded-none" data-testid="cleaning-form-notes" />
              </div>

              {/* Sections: Each section = 1 Cleaning Type + Cleaners */}
              <div className="space-y-3">
                {formData.sections.map((section, sIdx) => (
                  <div key={sIdx} className="border border-zinc-200 bg-zinc-50/50" data-testid={`cleaning-section-${sIdx}`}>
                    <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2">
                      <div className="flex-1">
                        <Select value={section.cleaning_type} onValueChange={(val) => {
                          const upd = [...formData.sections]; upd[sIdx] = { ...upd[sIdx], cleaning_type: val }; setFormData({ ...formData, sections: upd });
                        }}>
                          <SelectTrigger className="rounded-none h-8 text-xs font-semibold" data-testid={`cleaning-section-type-${sIdx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{cleaningTypes.map(ct => <SelectItem key={ct.key} value={ct.key}>{getTypeName(ct.key)}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {!editingEntry && formData.sections.length > 1 && (
                        <button type="button" onClick={() => setFormData({ ...formData, sections: formData.sections.filter((_, i) => i !== sIdx) })} className="p-1 text-red-500 hover:bg-red-50" title={t('delete')} data-testid={`remove-section-${sIdx}`}>
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                    <div className="p-3 space-y-2">
                      {section.cleaners.map((cleaner, cIdx) => (
                        <div key={cIdx} className="flex items-center gap-2" data-testid={`cleaner-row-${sIdx}-${cIdx}`}>
                          <div className="flex-1">
                            <Select value={cleaner.employee_name} onValueChange={(val) => {
                              const match = globalCleaners.find(c => c.name === val);
                              const updSections = [...formData.sections];
                              const updCleaners = [...updSections[sIdx].cleaners];
                              updCleaners[cIdx] = { ...updCleaners[cIdx], employee_name: val, cleaner_id: match ? match.cleaner_id : '' };
                              updSections[sIdx] = { ...updSections[sIdx], cleaners: updCleaners };
                              setFormData({ ...formData, sections: updSections });
                            }}>
                              <SelectTrigger className="rounded-none h-9" data-testid={`cleaning-form-cleaner-${sIdx}-${cIdx}`}><SelectValue placeholder={t('selectCleaner')} /></SelectTrigger>
                              <SelectContent>
                                {globalCleaners.map(c => <SelectItem key={c.cleaner_id} value={c.name}>{c.name}{c.phone ? ` (${c.phone})` : ''}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-28">
                            <Input type="number" step="0.01" min="0" value={cleaner.cleaner_payment} onChange={(e) => {
                              const updSections = [...formData.sections];
                              const updCleaners = [...updSections[sIdx].cleaners];
                              updCleaners[cIdx] = { ...updCleaners[cIdx], cleaner_payment: e.target.value };
                              updSections[sIdx] = { ...updSections[sIdx], cleaners: updCleaners };
                              setFormData({ ...formData, sections: updSections });
                            }} className="rounded-none h-9 font-mono" placeholder="€0.00" data-testid={`cleaning-form-payment-${sIdx}-${cIdx}`} />
                          </div>
                          {!editingEntry && section.cleaners.length > 1 && (
                            <button type="button" onClick={() => {
                              const updSections = [...formData.sections];
                              updSections[sIdx] = { ...updSections[sIdx], cleaners: updSections[sIdx].cleaners.filter((_, i) => i !== cIdx) };
                              setFormData({ ...formData, sections: updSections });
                            }} className="p-1 text-red-500 hover:bg-red-50" data-testid={`remove-cleaner-${sIdx}-${cIdx}`}>
                              <Trash size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                      {!editingEntry && (
                        <button type="button" onClick={() => {
                          const updSections = [...formData.sections];
                          updSections[sIdx] = { ...updSections[sIdx], cleaners: [...updSections[sIdx].cleaners, { employee_name: '', cleaner_id: '', cleaner_payment: '' }] };
                          setFormData({ ...formData, sections: updSections });
                        }} className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 mt-1" data-testid={`add-cleaner-to-section-${sIdx}`}>
                          <Plus size={12} /> {t('addAnotherCleaner')}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!editingEntry && (
                  <button type="button" onClick={() => setFormData({ ...formData, sections: [...formData.sections, { cleaning_type: 'general_cleaning', cleaners: [{ employee_name: '', cleaner_id: '', cleaner_payment: '' }] }] })} className="w-full py-2 border border-dashed border-zinc-300 text-xs text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700 flex items-center justify-center gap-1 transition-colors" data-testid="add-cleaning-type-btn">
                    <Plus size={14} /> {t('addCleaningType')}
                  </button>
                )}
              </div>

              {/* Grand Total */}
              {(() => {
                const allCleaners = formData.sections.flatMap(s => s.cleaners);
                const total = allCleaners.reduce((sum, c) => sum + (parseFloat(c.cleaner_payment) || 0), 0);
                return total > 0 ? (
                  <div className="flex items-center justify-between px-3 py-2 bg-zinc-100 border border-zinc-200">
                    <span className="text-xs font-semibold text-zinc-600">{t('totalPayment')}</span>
                    <span className="font-mono font-bold text-sm" data-testid="cleaning-form-total">&euro;{total.toFixed(2)}</span>
                  </div>
                ) : null;
              })()}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-cleaning-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* DELETE ENTRY DIALOG */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('deleteCleaningEntry')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeleteCleaning')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-cleaning-btn">{t('delete')}</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* MANAGE TYPES MODAL */}
        <Dialog open={typeMgmtOpen} onOpenChange={setTypeMgmtOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{t('manageTypes')}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {cleaningTypes.map(ct => (
                <div key={ct.type_id} className="flex items-center justify-between p-3 border border-zinc-200 bg-zinc-50" data-testid={`type-row-${ct.key}`}>
                  <div>
                    <span className={`px-2 py-0.5 text-xs font-mono border ${getTypeColor(ct.key, cleaningTypes)}`}>{language === 'el' ? (ct.name_el || ct.name_en) : ct.name_en}</span>
                    <span className="text-xs text-zinc-400 ml-2 font-mono">{ct.key}</span>
                    {ct.threshold_days > 0 && <span className="text-xs text-zinc-400 ml-2">({ct.threshold_days}d)</span>}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingType(ct); setTypeForm({ key: ct.key, name_en: ct.name_en, name_el: ct.name_el || '', threshold_days: ct.threshold_days || 0 }); }} className="p-1.5 hover:bg-white text-zinc-500"><PencilSimple size={16} /></button>
                    <button onClick={() => { setDeletingType(ct); setDeleteTypeDialogOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-500"><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={handleSaveType} className="space-y-3 border-t border-zinc-200 pt-4 mt-4">
              <p className="text-sm font-semibold">{editingType ? t('editType') : t('addType')}</p>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">{t('typeNameEn')}</Label><Input value={typeForm.name_en} onChange={(e) => setTypeForm({ ...typeForm, name_en: e.target.value })} className="rounded-none" required data-testid="type-name-en-input" /></div>
                <div><Label className="text-xs">{t('typeNameEl')}</Label><Input value={typeForm.name_el} onChange={(e) => setTypeForm({ ...typeForm, name_el: e.target.value })} className="rounded-none" data-testid="type-name-el-input" /></div>
              </div>
              <div>
                <Label className="text-xs">{t('thresholdDays')}</Label>
                <Input type="number" min="0" value={typeForm.threshold_days} onChange={(e) => setTypeForm({ ...typeForm, threshold_days: parseInt(e.target.value) || 0 })} className="rounded-none w-32" data-testid="type-threshold-input" />
              </div>
              <DialogFooter>
                {editingType && <Button type="button" variant="outline" onClick={() => { setEditingType(null); setTypeForm({ key: '', name_en: '', name_el: '', threshold_days: 0 }); }} className="rounded-none">{t('cancel')}</Button>}
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-type-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* DELETE TYPE DIALOG */}
        <AlertDialog open={deleteTypeDialogOpen} onOpenChange={setDeleteTypeDialogOpen}>
          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t('deleteType')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeleteType')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel><AlertDialogAction onClick={handleDeleteType} className="bg-red-600 rounded-none" data-testid="confirm-delete-type-btn">{t('delete')}</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* CLEANER STATEMENT DIALOG */}
        <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
          <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="statement-dialog">
            <DialogHeader><DialogTitle>{t('cleanerStatement')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <Label className="text-xs">{t('cleanerName')}</Label>
                  <Select value={stmtCleaner || 'all'} onValueChange={v => setStmtCleaner(v === 'all' ? '' : v)}>
                    <SelectTrigger className="rounded-none h-9 w-44 text-xs" data-testid="stmt-cleaner"><SelectValue placeholder={t('allCleaners')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('allCleaners')}</SelectItem>
                      {globalCleaners.map(c => <SelectItem key={c.cleaner_id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t('fromDate')}</Label>
                  <DatePickerDMY value={stmtFrom} onChange={setStmtFrom} className="rounded-none h-9 w-40 text-xs font-mono" data-testid="stmt-from" />
                </div>
                <div>
                  <Label className="text-xs">{t('toDate')}</Label>
                  <DatePickerDMY value={stmtTo} onChange={setStmtTo} className="rounded-none h-9 w-40 text-xs font-mono" data-testid="stmt-to" />
                </div>
                <Button onClick={loadStatement} size="sm" className="rounded-none h-9" disabled={stmtLoading} data-testid="stmt-load">{stmtLoading ? '...' : t('search')}</Button>
                <button onClick={handleDownloadStatementPdf} className="inline-flex items-center justify-center rounded-none h-9 px-3 border border-zinc-200 bg-white text-sm hover:bg-zinc-50" data-testid="stmt-pdf"><FilePdf size={16} className="mr-1" /> PDF</button>
              </div>

              {stmtData && stmtData.length === 0 && <p className="text-center text-zinc-400 py-8 text-sm">{t('noData')}</p>}
              {stmtData && stmtData.map((cleaner, ci) => (
                <div key={ci} className="border border-zinc-200 bg-white" data-testid={`stmt-cleaner-${ci}`}>
                  <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserCircle size={24} className="text-zinc-400" />
                      <div>
                        <h3 className="font-semibold text-zinc-900">{cleaner.cleaner_name}</h3>
                        <p className="text-xs text-zinc-500">{cleaner.total_jobs} {t('totalJobs').toLowerCase()}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-zinc-400">{t('totalEarned')}</p>
                      <p className="text-xl font-bold font-mono text-emerald-700">&euro;{cleaner.total_earned?.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    {cleaner.jobs && cleaner.jobs.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-zinc-500 uppercase mb-2">{t('jobHistory')}</h4>
                        <div className="border border-zinc-200 overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead><tr className="bg-zinc-50 border-b text-zinc-400">
                              <th className="text-left px-3 py-1.5">{t('date')}</th>
                              <th className="text-left px-3 py-1.5">{t('buildings')}</th>
                              <th className="text-right px-3 py-1.5">{t('cleanerEarned')}</th>
                            </tr></thead>
                            <tbody>{cleaner.jobs.map((j, ji) => (
                              <tr key={ji} className="border-b border-zinc-100">
                                <td className="px-3 py-1 font-mono">{formatDateDMY(j.date)}</td>
                                <td className="px-3 py-1">{j.building_name}</td>
                                <td className="px-3 py-1 text-right font-mono text-emerald-700">&euro;{j.cleaner_earned?.toFixed(2)}</td>
                              </tr>
                            ))}</tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {cleaner.buildings?.map((b, bi) => (
                      <div key={bi} className="border border-zinc-100 bg-zinc-50/50">
                        <div className="px-3 py-2 flex items-center justify-between">
                          <span className="text-sm font-medium">{b.building_name} <span className="text-xs text-zinc-400">({b.visits} {language === 'el' ? 'visits' : 'visits'})</span></span>
                          <span className="font-mono font-semibold text-sm text-emerald-700">&euro;{b.total_earned?.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStmtOpen(false)} className="rounded-none">{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
  );

  if (embedded) return content;
  return <Layout>{content}</Layout>;
}
